import React from "react";

const RefundPolicy = () => {
  return (
    <div className="min-h-screen px-4 py-12 bg-gray-50 sm:px-6 lg:px-8">
      <div className="max-w-4xl p-8 mx-auto bg-white rounded-lg shadow-lg md:p-12">
        {/* Header */}
        <header className="pb-6 mb-8 border-b border-gray-200">
          <h1 className="mb-3 text-4xl font-bold text-gray-900">
            Refund Policy
          </h1>
          <p className="text-sm text-gray-500">Last updated: 20 January 2025</p>
        </header>

        {/* Introduction */}
        <section className="mb-8">
          <p className="mb-4 leading-relaxed text-gray-700">
            Thank you for choosing Inhocsigno Hominum Salvator (ISA Academy).
          </p>
          <p className="leading-relaxed text-gray-700">
            Because our services are delivered digitally and/or provided through
            scheduled sessions (training, consultation, and webinars), our
            refund policy is different from physical product purchases. Please
            read this policy carefully before making any purchase.
          </p>
        </section>

        {/* Section 1 */}
        <section className="mb-8">
          <h2 className="mb-4 text-2xl font-semibold text-gray-900">
            1. General Policy
          </h2>
          <p className="mb-4 leading-relaxed text-gray-700">
            All sales of digital services are final once access has been
            granted, materials have been delivered, or a session has begun.
          </p>
          <p className="mb-3 text-gray-700">We do not offer refunds for:</p>
          <ul className="ml-4 space-y-1 text-gray-700 list-disc list-inside">
            <li>Completed training or webinar sessions</li>
            <li>Completed consultation sessions</li>
            <li>
              Digital materials already delivered (e.g., slides, recordings,
              documents)
            </li>
            <li>Missed sessions due to personal scheduling conflicts</li>
            <li>User's technical issues (internet, device, software)</li>
          </ul>
        </section>

        {/* Section 2 */}
        <section className="mb-8">
          <h2 className="mb-4 text-2xl font-semibold text-gray-900">
            2. Refund Eligibility
          </h2>
          <p className="mb-4 leading-relaxed text-gray-700">
            You may be eligible for a refund only under the following
            conditions:
          </p>

          <div className="mb-4 ml-4">
            <h3 className="mb-3 text-xl font-medium text-gray-800">
              2.1 Technical Issues (Service Failure)
            </h3>
            <p className="mb-3 text-gray-700">
              If the service could not be delivered due to an error on our side,
              such as:
            </p>
            <ul className="mb-4 ml-4 space-y-1 text-gray-700 list-disc list-inside">
              <li>Platform failure caused by our system</li>
              <li>Trainer unable to attend a live session</li>
              <li>Payment charged multiple times</li>
            </ul>
            <p className="mb-2 text-gray-700">In such cases, we will offer:</p>
            <ul className="ml-4 space-y-1 text-gray-700 list-disc list-inside">
              <li>Full refund or</li>
              <li>Rescheduling / replacement session</li>
            </ul>
          </div>

          <div className="ml-4">
            <h3 className="mb-3 text-xl font-medium text-gray-800">
              2.2 Duplicate Purchase
            </h3>
            <p className="text-gray-700">
              If you accidentally purchased the same service twice, we can issue
              a full refund for the duplicate order.
            </p>
          </div>
        </section>

        {/* Section 3 */}
        <section className="mb-8">
          <h2 className="mb-4 text-2xl font-semibold text-gray-900">
            3. Rescheduling Policy (No Refund)
          </h2>

          <div className="mb-4">
            <p className="mb-2 font-medium text-gray-700">
              For live consultation sessions:
            </p>
            <ul className="ml-4 space-y-1 text-gray-700 list-disc list-inside">
              <li>
                You may reschedule once, at least 24 hours before the session
                time.
              </li>
              <li>
                If you fail to attend the session or cancel within 24 hours, the
                session is considered completed and non-refundable.
              </li>
            </ul>
          </div>

          <div>
            <p className="mb-2 font-medium text-gray-700">
              For live training or webinar events:
            </p>
            <ul className="ml-4 space-y-1 text-gray-700 list-disc list-inside">
              <li>
                If a replay/recording is provided, refunds are not available.
              </li>
              <li>
                If you cannot attend, you may request access to recordings (if
                offered) or join another batch if available.
              </li>
            </ul>
          </div>
        </section>

        {/* Section 4 */}
        <section className="mb-8">
          <h2 className="mb-4 text-2xl font-semibold text-gray-900">
            4. Refund Process
          </h2>
          <p className="mb-4 leading-relaxed text-gray-700">
            If you qualify for a refund based on the conditions above:
          </p>

          <div className="p-4 mb-4 border-l-4 border-blue-500 bg-blue-50">
            <p className="mb-2 text-gray-800">
              <span className="font-medium">Email your request to:</span>{" "}
              <a
                href="mailto:company@sustainability.co.id"
                className="text-blue-600 underline hover:text-blue-800"
              >
                company@sustainability.co.id
              </a>
            </p>
            <p className="mb-2 font-medium text-gray-800">Include your:</p>
            <ul className="ml-4 space-y-1 text-gray-700 list-disc list-inside">
              <li>Full name</li>
              <li>Order ID</li>
              <li>Proof of payment</li>
              <li>Reason for refund</li>
            </ul>
          </div>

          <p className="mb-3 leading-relaxed text-gray-700">
            We will review your request within{" "}
            <span className="font-medium">5–7 business days</span>.
          </p>
          <p className="leading-relaxed text-gray-700">
            Approved refunds will be processed to the original payment method
            within <span className="font-medium">7–14 business days</span>,
            depending on your bank or payment provider.
          </p>
        </section>

        {/* Section 5 */}
        <section className="mb-8">
          <h2 className="mb-4 text-2xl font-semibold text-gray-900">
            5. Non-Refundable Services
          </h2>
          <p className="mb-3 text-gray-700">Refunds will not be granted for:</p>
          <ul className="ml-4 space-y-1 text-gray-700 list-disc list-inside">
            <li>Change of mind after purchase</li>
            <li>Failure to attend live events</li>
            <li>Failure to use the delivered digital service</li>
            <li>
              Technical issues on the user's side (internet, device, software,
              etc.)
            </li>
            <li>
              Access to recordings, materials, or digital content already
              provided
            </li>
            <li>
              Customized/one-on-one consultation sessions after the time slot is
              confirmed
            </li>
          </ul>
        </section>

        {/* Section 6 - Contact */}
        <section className="mb-8">
          <h2 className="mb-4 text-2xl font-semibold text-gray-900">
            6. Contact Us
          </h2>
          <p className="mb-4 text-gray-700">
            If you have questions about our Refund Policy, contact us at:
          </p>
          <div className="p-6 border border-gray-200 rounded-lg bg-gray-50">
            <p className="mb-2 font-medium text-gray-900">
              PT Inhocsigno Hominum Salvator
            </p>
            <p className="mb-1 text-gray-700">
              <span className="font-medium">Email:</span>{" "}
              <a
                href="mailto:emil@sustainability.co.id"
                className="text-blue-600 underline hover:text-blue-800"
              >
                emil@sustainability.co.id
              </a>
            </p>
            <p className="mb-1 text-gray-700">
              <span className="font-medium">Phone:</span>{" "}
              <a
                href="tel:+6281212276133"
                className="text-blue-600 hover:text-blue-800"
              >
                +62 812-1227-6133
              </a>
            </p>
            <p className="text-gray-700">
              <span className="font-medium">Website:</span>{" "}
              <a
                href="https://isaacademy.co.id"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 underline hover:text-blue-800"
              >
                isaacademy.co.id
              </a>
            </p>
          </div>
        </section>
      </div>
    </div>
  );
};

export default RefundPolicy;
